import React, { ReactNode } from 'react'
import { Box, Typography, Chip, IconButton, Tooltip } from '@mui/material'
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined'
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'
import { FolderIcon } from './FolderIcon'
import { FS_XS, FS_MICRO, ICON_SM, ICON_XS } from '../common/theme'
import type { ScopeItem } from '../types'

interface SearchingScopeProps {
  // Multi-select items - when non-empty, they're listed as removable chips
  items?: ScopeItem[]
  onRemoveItem?: (sha: string) => void
  onClear?: () => void
  // Current-nav scope (shown alongside items when not at root)
  currentPath?: string
  currentDirName?: string | null
  currentFolderIsProject?: boolean
  // Trailing hint (only shown when there's no selection and at root)
  hint?: ReactNode
}

export default function SearchingScope({
  items, onRemoveItem, onClear,
  currentPath, currentDirName, currentFolderIsProject,
  hint,
}: SearchingScopeProps) {
  const hasItems = !!items && items.length > 0
  const isRoot = !currentPath || currentPath === '/'
  const hasFolder = !isRoot && !!currentDirName
  const hasScope = hasItems || hasFolder

  const chipSx = {
    height: 20, fontSize: FS_MICRO,
    '& .MuiChip-label': { px: 0.75 },
    '& .MuiChip-icon': { ml: 0.5, mr: -0.25 },
  }

  if (hasScope) {
    // If the user has explicit selections, those take over the scope display.
    // Otherwise, fall back to a single non-removable chip for the navigated folder.
    const parts: ReactNode[] = []
    if (hasItems) {
      for (const info of items!) {
        parts.push(
          <Chip
            key={info.sha}
            size="small"
            data-testid="scope_chip"
            data-scope-sha={info.sha}
            data-scope-name={info.name}
            data-scope-type={info.type}
            icon={info.type === 'folder'
              ? <FolderIcon isProject={info.isProject} sx={{ scale: 0.5, mr: -1 }} />
              : <InsertDriveFileOutlinedIcon sx={{ fontSize: ICON_XS, color: 'info.main' }} />}
            label={info.name}
            onDelete={onRemoveItem ? () => onRemoveItem(info.sha) : undefined}
            sx={chipSx}
          />
        )
      }
    } else if (hasFolder) {
      parts.push(
        <Chip
          key="__nav-folder"
          size="small"
          data-testid="scope_navfolder_chip"
          data-scope-name={currentDirName ?? ''}
          icon={<FolderIcon isProject={currentFolderIsProject} sx={{ scale: 0.5, mr: -1 }} />}
          label={currentDirName}
          sx={chipSx}
        />
      )
    }

    return (
      <Box data-testid="scope_panel" sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.4, flex: 1, minWidth: 0 }}>
        <MenuBookOutlinedIcon sx={{ fontSize: ICON_SM, color: 'primary.main', flexShrink: 0, mt: -0.25, mr: 0.5 }} />
        <Typography variant="caption" sx={{ fontSize: FS_XS, color: 'text.secondary', mr: 0.25 }}>
          Searching
        </Typography>
        {parts.map((node, i) => (
          <React.Fragment key={(node as React.ReactElement).key ?? i}>
            {i > 0 && (
              <Typography variant="caption" sx={{ fontSize: FS_XS, color: 'text.disabled', mx: 0.25 }}>
                &
              </Typography>
            )}
            {node}
          </React.Fragment>
        ))}
        {hasItems && onClear && (
          <Tooltip title="Clear selection">
            <IconButton size="small" data-testid="scope_clear_btn" onClick={onClear} sx={{ p: 0.25, ml: 0.25 }}>
              <Typography variant="caption" sx={{ fontSize: FS_MICRO, color: 'primary.main', lineHeight: 1 }}>Clear</Typography>
            </IconButton>
          </Tooltip>
        )}
      </Box>
    )
  }

  return (
    <Box data-testid="scope_panel" sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
      <MenuBookOutlinedIcon sx={{ fontSize: ICON_SM, opacity: 0.4, mt: -0.25, mr: 0.5 }} />
      <Typography variant="caption" sx={{ fontSize: FS_XS, color: 'text.secondary', lineHeight: 1 }}>
        Searching Knowledge Base
      </Typography>
      {hint && (
        <Typography variant="caption" sx={{ fontSize: FS_XS, color: 'text.disabled', userSelect: 'none', lineHeight: 1 }}>
          {hint}
        </Typography>
      )}
    </Box>
  )
}
